import { useEffect, useState } from "react";
import type { ComponentType, SVGProps } from "react";
import { SectionTitle } from "../_shared/section-title";
import { PageShell, Stack } from "../_shared/page-shell";
import {
  IconActivity,
  IconAligner,
  IconBolt,
  IconCalendar,
  IconCamera,
  IconChevronRight,
  IconClipboard,
  IconDocument,
  IconHeart,
  IconHelp,
  IconHouse,
  IconImages,
  IconInfo,
  IconMessage,
  IconSearchLarge,
  IconSignature,
  IconWallet,
} from "../_shared/icons";
import { navigate, useRoute, type Route } from "../_shared/router";

type IconCmp = ComponentType<SVGProps<SVGSVGElement>>;

type Entry = {
  id: string;
  route: Route["name"];
  label: string;
  description: string;
  keywords: string[];
  Icon: IconCmp;
};

const entries: Entry[] = [
  {
    id: "summary",
    route: "summary",
    label: "Résumé",
    description: "Tableau de bord, éléments à traiter",
    keywords: ["accueil", "dashboard", "à faire", "actions"],
    Icon: IconHouse,
  },
  {
    id: "rdv",
    route: "appointments",
    label: "Rendez-vous",
    description: "Prochains, historique, prise de RDV",
    keywords: ["rdv", "calendrier", "consultation", "agenda"],
    Icon: IconCalendar,
  },
  {
    id: "book",
    route: "book",
    label: "Prendre rendez-vous",
    description: "Choisir un motif et un créneau",
    keywords: ["nouveau rdv", "reservation", "creneau", "demande"],
    Icon: IconCalendar,
  },
  {
    id: "paiements",
    route: "payments",
    label: "Paiements",
    description: "Solde, échéances, historique",
    keywords: ["facture", "argent", "devis", "regle"],
    Icon: IconWallet,
  },
  {
    id: "documents",
    route: "documents",
    label: "Documents",
    description: "Reçus, manquants, téléversement",
    keywords: ["secu", "mutuelle", "carte vitale", "fichier", "pdf"],
    Icon: IconDocument,
  },
  {
    id: "photos",
    route: "photos",
    label: "Photos",
    description: "Suivi du traitement en images",
    keywords: ["images", "galerie"],
    Icon: IconImages,
  },
  {
    id: "signatures",
    route: "signatures",
    label: "Signatures",
    description: "Documents à signer",
    keywords: ["signer", "consentement", "cgu", "devis"],
    Icon: IconSignature,
  },
  {
    id: "seances-photos",
    route: "photo-sessions",
    label: "Séances photos",
    description: "Protocoles clichés cabinet",
    keywords: ["clichés", "planche", "intra-buccale", "portrait"],
    Icon: IconCamera,
  },
  {
    id: "aligneurs",
    route: "aligners",
    label: "Aligneurs",
    description: "Progression et étapes du traitement",
    keywords: ["gouttières", "invisalign", "etapes"],
    Icon: IconAligner,
  },
  {
    id: "exercices",
    route: "exercises",
    label: "Exercices",
    description: "Exercices quotidiens, série en cours",
    keywords: ["brossage", "elastiques", "routine"],
    Icon: IconActivity,
  },
  {
    id: "astuces",
    route: "astuces",
    label: "Astuces",
    description: "Conseils & FAQ du cabinet",
    keywords: ["aide", "questions", "tips", "faq"],
    Icon: IconInfo,
  },
  {
    id: "cabinet",
    route: "cabinet",
    label: "Cabinet",
    description: "Adresse, équipe, horaires",
    keywords: ["coordonnées", "carte", "telephone", "praticien"],
    Icon: IconHouse,
  },
  {
    id: "messages",
    route: "messages",
    label: "Messages",
    description: "Discuter avec le cabinet",
    keywords: ["chat", "ecrire", "questions"],
    Icon: IconMessage,
  },
  {
    id: "urgence",
    route: "emergency",
    label: "Photo d'urgence",
    description: "Envoyer une photo d'urgence au cabinet",
    keywords: ["bague", "douleur", "casse", "urgence"],
    Icon: IconBolt,
  },
  {
    id: "contact",
    route: "contact",
    label: "Contact",
    description: "Joindre le cabinet, actions rapides",
    keywords: ["telephone", "mail", "absence", "annuler"],
    Icon: IconHelp,
  },
  {
    id: "temoignage",
    route: "summary",
    label: "Témoignage",
    description: "Laisser un avis sur le traitement",
    keywords: ["avis", "feedback"],
    Icon: IconHeart,
  },
  {
    id: "questionnaire",
    route: "summary",
    label: "Questionnaires",
    description: "Questionnaires à remplir",
    keywords: ["evaluation", "douleur", "formulaire"],
    Icon: IconClipboard,
  },
];

function normalize(s: string) {
  return s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
}

function score(entry: Entry, q: string) {
  if (!q) return 1;
  const nq = normalize(q);
  const label = normalize(entry.label);
  const desc = normalize(entry.description);
  const kws = entry.keywords.map(normalize);
  if (label.startsWith(nq)) return 3;
  if (label.includes(nq)) return 2.5;
  if (kws.some((k) => k.startsWith(nq))) return 2;
  if (kws.some((k) => k.includes(nq))) return 1.5;
  if (desc.includes(nq)) return 1;
  return 0;
}

export function SearchPage() {
  const { searchQuery } = useRoute();
  const [query, setQuery] = useState(searchQuery);

  useEffect(() => {
    setQuery(searchQuery);
  }, [searchQuery]);

  const matched = entries
    .map((e) => ({ entry: e, s: score(e, query) }))
    .filter((m) => m.s > 0)
    .sort((a, b) => {
      if (!query) return a.entry.label.localeCompare(b.entry.label, "fr");
      if (b.s !== a.s) return b.s - a.s;
      return a.entry.label.localeCompare(b.entry.label, "fr");
    })
    .map((m) => m.entry);

  return (
    <>
      <SectionTitle title="Rechercher" showBack={false} />
      <PageShell>
        <Stack>
          <label className="hidden h-12 items-center gap-2 rounded-full bg-[var(--surface-1)] px-4 shadow-[0_1px_2px_rgba(15,23,42,0.06)] ring-1 ring-[var(--border-1)] focus-within:ring-2 focus-within:ring-[var(--sf-primary)] lg:flex">
            <IconSearchLarge
              width={18}
              height={18}
              className="text-[var(--text-3)]"
            />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher dans vos contenus…"
              className="min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-[var(--text-3)]"
            />
          </label>

          {matched.length === 0 ? (
            <p className="px-1 py-4 text-center text-[14px] text-[var(--text-2)]">
              Aucun résultat pour « {query} ».
            </p>
          ) : (
            <ul className="space-y-2">
              {matched.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => navigate(s.route)}
                    className="flex w-full items-center gap-3 rounded-2xl bg-[var(--surface-1)] p-3.5 text-left shadow-[0_1px_3px_rgba(15,23,42,0.04)] ring-1 ring-[var(--border-1)] transition hover:bg-[var(--surface-2)] active:scale-[0.99]"
                  >
                    <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-xl bg-[var(--sf-primary-100)] text-[var(--sf-primary)]">
                      <s.Icon width={20} height={20} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[15px] font-semibold text-[var(--text-1)]">
                        {s.label}
                      </p>
                      <p className="mt-0.5 truncate text-[12.5px] text-[var(--text-2)]">
                        {s.description}
                      </p>
                    </div>
                    <IconChevronRight
                      width={16}
                      height={16}
                      className="shrink-0 text-[var(--text-3)]"
                    />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Stack>
      </PageShell>
    </>
  );
}
